import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";

const KINDS = ["suggestion", "complaint", "grievance"];

export default function Tickets() {
  const [rows, setRows] = useState([]);
  const [kindFilter, setKindFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(null);

  const load = async () => {
    const r = await api.get("/tickets", { params: kindFilter ? { kind: kindFilter } : {} });
    setRows(r.data);
  };
  useEffect(() => { load(); }, [kindFilter]);

  const create = async () => {
    if (!creating.title) return toast.warning("Title required");
    await api.post("/tickets", creating);
    toast.success("Created"); setCreating(null); load();
  };

  const save = async () => {
    await api.patch(`/tickets/${editing.ticket_id}`, {
      status: editing.status, assigned_to: editing.assigned_to, resolution_note: editing.resolution_note,
    });
    toast.success("Updated"); setEditing(null); load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Tickets</h1>
          <p className="text-sm text-[#A1A1AA] mt-1">Complaints, suggestions and grievances. SLA 3 working days.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} className="tt-input !w-auto text-sm">
            <option value="">All kinds</option>
            {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <button onClick={() => setCreating({ kind: "suggestion", title: "", body: "", public_board: false })} className="tt-btn-primary flex items-center gap-2"><Plus size={14} /> New</button>
        </div>
      </div>

      <div className="tt-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0F0F0F]">
            <tr className="text-[#71717A] text-xs font-mono">
              <th className="text-left px-4 py-2">Title</th>
              <th className="text-left px-4 py-2">Kind</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">SLA</th>
              <th className="text-left px-4 py-2">Created</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.ticket_id} className="border-t border-[#1E1E1E] hover:bg-[#121212] cursor-pointer" onClick={() => setEditing({ ...r })}>
                <td className="px-4 py-3">
                  <div className="font-medium">{r.title}</div>
                  <div className="text-xs text-[#71717A]">{r.anonymous ? "anonymous" : (r.employee_id || "—")}</div>
                </td>
                <td className="px-4 py-3"><span className="tt-badge-neutral">{r.kind}</span></td>
                <td className="px-4 py-3">
                  <span className={r.status === "resolved" || r.status === "closed" ? "tt-badge-pass" : r.status === "in_progress" ? "tt-badge-info" : "tt-badge-warn"}>{r.status}</span>
                </td>
                <td className="px-4 py-3">{r.sla_overdue ? <span className="tt-badge-danger">overdue</span> : <span className="font-mono text-xs text-[#71717A]">{r.sla_due_at?.slice(0, 10)}</span>}</td>
                <td className="px-4 py-3 font-mono text-xs text-[#71717A]">{new Date(r.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-[#71717A]">No tickets.</td></tr>}
          </tbody>
        </table>
      </div>

      {creating && (
        <div className="fixed inset-0 bg-[rgba(10,10,10,0.85)] flex items-center justify-center z-40 p-4" onClick={() => setCreating(null)}>
          <div className="w-full max-w-lg tt-card p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between mb-4">
              <h2 className="text-lg font-semibold">New ticket</h2>
              <button onClick={() => setCreating(null)} className="tt-btn-ghost"><X size={16} /></button>
            </div>
            <select value={creating.kind} onChange={(e) => setCreating({ ...creating, kind: e.target.value })} className="tt-input mb-3">
              {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <input className="tt-input mb-3" placeholder="Title" value={creating.title} onChange={(e) => setCreating({ ...creating, title: e.target.value })} />
            <textarea rows={4} className="tt-input mb-3" placeholder="Details" value={creating.body} onChange={(e) => setCreating({ ...creating, body: e.target.value })} />
            <label className="flex items-center gap-2 text-sm mb-4"><input type="checkbox" className="accent-[#5B9BFF]" checked={creating.public_board} onChange={(e) => setCreating({ ...creating, public_board: e.target.checked })} /> Public suggestions board</label>
            <div className="flex justify-end gap-2"><button onClick={() => setCreating(null)} className="tt-btn-secondary text-sm">Cancel</button><button onClick={create} className="tt-btn-primary text-sm">Create</button></div>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-[rgba(10,10,10,0.85)] flex items-center justify-center z-40 p-4" onClick={() => setEditing(null)}>
          <div className="w-full max-w-lg tt-card p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between mb-4">
              <h2 className="text-lg font-semibold">{editing.title}</h2>
              <button onClick={() => setEditing(null)} className="tt-btn-ghost"><X size={16} /></button>
            </div>
            <div className="text-sm text-[#A1A1AA] mb-4 whitespace-pre-wrap">{editing.body || "—"}</div>
            <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="tt-input mb-3">
              {["open", "acknowledged", "in_progress", "resolved", "closed"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <input className="tt-input mb-3" placeholder="Assigned to (email)" value={editing.assigned_to || ""} onChange={(e) => setEditing({ ...editing, assigned_to: e.target.value })} />
            <textarea rows={3} className="tt-input mb-4" placeholder="Resolution note" value={editing.resolution_note || ""} onChange={(e) => setEditing({ ...editing, resolution_note: e.target.value })} />
            <div className="flex justify-end gap-2"><button onClick={() => setEditing(null)} className="tt-btn-secondary text-sm">Cancel</button><button onClick={save} className="tt-btn-primary text-sm">Save</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
